<template>
    <div class="chart-container">
        <NoDataOverlay :isShown="!hasData && !loading">
            <slot name="noDataOverlaySubText" slot="subText"/>
        </NoDataOverlay>
        <Loader :status="loading"></Loader>
        <div ref="chart" class="chart" v-show="hasData && !loading"></div>
        <Legend :options="legendOptions"
                :selected="totalMode ? selectedRows : selectedColumns"
                :colors="legendColors"
                :maxSelected="maxSelected"
                @highlight="highlight"
                @clearHighlight="clearColumnHighlight"
                @checked="legendOptionChecked"></Legend>
    </div>
</template>

<script>
import { debounce, merge } from 'lodash';
import moment from 'moment';
import NoDataOverlay from 'components/noDataOverlay/noDataOverlay.vue';
import Loader from 'components/loader/loader.vue';
import Legend from 'components/charts/Legend.vue';
import { getColor, getColors } from 'components/charts/chartColors';
import {
    mediumDateFormat, monthFormat, weekFormat
} from 'services/utils/dates';
import { chartTypes } from 'components/charts/chartTypes';
import { chartsVersion } from './googleChartsSettings';
import loadGoogleCharts from './googleChartsLoader';

let chartsLib = null;
export default {
    name: 'Chart',
    components: {
        Loader,
        NoDataOverlay,
        Legend
    },
    props: {
        data: {
            type: Object
        },
        version: {
            type: String,
            default: chartsVersion
        },
        settings: {
            type: Object,
            default: () => ({
                packages: ['corechart', 'table']
            })
        },
        events: {
            type: Object
        },
        createChart: {
            type: Function
        },
        resizeDebounce: {
            type: Number,
            default: 200
        },
        title: {
            type: String,
            default: 'Time'
        },
        type: {
            type: String,
            default: chartTypes.LineChart,
            validate: (type) => Object.values(chartTypes).includes(type)
        },
        options: {
            type: Object,
            default: () => ({})
        },
        suffix: {
            type: String,
            default: ''
        },
        prefix: {
            type: String,
            default: ''
        },
        // the group represents the data type (Break By), each group will have it's own color assignments
        group: {
            type: String,
            default: 'default'
        },
        maxSelected: {
            type: Number,
            default: 10
        },
        showTextEvery: {
            type: Number,
            default: 0
        },
        hAxisFormat: {
            type: String,
            default: 'string',
            validate: (type) => ['string', 'number'].includes(type)
        },
        totalMode: {
            type: Boolean,
            default: false
        },
        dateFormat: {
            type: String,
            default: 'day',
            validate: (type) => ['day', 'week', 'month'].includes(type)
        },
        animate: {
            type: Boolean,
            default: false
        },
        stacked: {
            type: Boolean,
            default: false
        },
        showAllHAxisValues: {
            type: Boolean,
            default: false
        },
        mean: {
            type: Boolean,
            default: false
        },
        dateColumns: {
            type: Boolean,
            default: true
        },
        preserveSelection: {
            type: Boolean,
            default: true
        },
        loading: {
            type: Boolean
        }
    },
    data() {
        return {
            chartObject: null,
            dataTable: null,
            viewData: null,
            previousViewData: null,
            previousGroup: undefined,
            previousTotalMode: undefined,
            selectedColumns: [],
            selectedRows: [],
            sumFieldName: 'sum'
        };
    },
    watch: {
        data: {
            handler(newValue) {
                if (!newValue) return;

                this.initDataTable();
                this.initSelection();
                this.drawChart({ preventDefaultAnimation: this.type !== 'LineChart' });
            }
        },
        options: {
            handler() {
                this.drawChart();
            }
        },
        totalMode: {
            handler(totalMode) {
                const previousSelectedColumns = this.selectedColumns;
                const previousSelectedRows = this.selectedRows;
                this.previousGroup = this.group;
                this.previousTotalMode = this.totalMode;
                this.initDataTable();

                // when toggling totalMode, the selected rows and columns need to be swapped respectively
                if (totalMode) {
                    this.selectedColumns = ['totals', 'style'];
                    this.selectedRows = [...previousSelectedColumns];
                    this.setSelectedRows();
                } else {
                    this.selectedColumns = [...previousSelectedRows];
                    this.setSelectedColumns();
                }

                this.drawChart({ preventDefaultAnimation: true });
            }
        },
        type(newType, oldType) {
            this.createChartObject();

            if (newType !== 'ColumnChart' && oldType !== 'ColumnChart') {
                this.drawChart({ preventDefaultAnimation: newType !== 'LineChart' });
            }
        }
    },
    mounted() {
        loadGoogleCharts(this.version, this.settings).then((api) => {
            chartsLib = api;
            const chart = this.createChartObject();
            this.$emit('ready', chart, api);
            this.drawChart();
        });
        this.resizeDebounceFunction = debounce(this.drawChart, this.resizeDebounce);
        if (this.resizeDebounce > 0) window.addEventListener('resize', this.resizeDebounceFunction);
    },
    beforeDestroy() {
        if (this.chartObject && typeof this.chartObject.clearChart === 'function') {
            this.chartObject.clearChart();
        }

        if (this.resizeDebounce > 0) window.removeEventListener('resize', this.resizeDebounceFunction);
    },
    methods: {
        legendOptionChecked(checkOptions) {
            if (!this.totalMode) {
                this.columnChecked(checkOptions);
            } else {
                this.rowChecked(checkOptions);
            }
        },
        columnChecked({ option, checked }) {
            if (checked) {
                this.selectedColumns = this.chartData.cols
                    .filter((col) => col.id === option || this.selectedColumns.includes(col.id))
                    .map((col) => col.id);
            } else {
                this.selectedColumns = this.selectedColumns.filter((col) => col !== option);
            }

            this.setSelectedColumns();
            this.drawChart({ preventLineCustomAnimation: true });
        },
        rowChecked({ option, checked }) {
            if (checked) {
                this.selectedRows.push(option);
            } else {
                this.selectedRows = this.selectedRows.filter((col) => col !== option);
            }

            this.setSelectedRows();
            this.drawChart();
        },
        setSelectedColumns(viewData = this.viewData) {
            if (!viewData || !this.hasData) {
                return;
            }

            const now = new Date();
            const selectedColumns = [0];

            // set the columns to be shown in the data view
            this.selectedColumns.forEach((visibleColumn) => {
                const visibleColumnIndex = this.chartData.cols.findIndex((col) => col.id === visibleColumn);
                selectedColumns.push(visibleColumnIndex);

                // add a dynamic column for the uncertainty line (if the last day/week/month is today)
                selectedColumns.push({
                    role: 'certainty',
                    calc: (dt, row) => {
                        const rowValue = dt.getValue(row, 0);

                        if (typeof rowValue !== 'string') {
                            return true;
                        }

                        const date = moment(rowValue, this.momentDateFormat);
                        const isToday = date.isSame(now, this.dateFormat);
                        const certain = !isToday;

                        return certain;
                    },
                    type: 'boolean'
                });

                // add a dynamic column for a tooltip for month/week date formats
                if (this.dateFormat !== 'day' && !this.totalMode) {
                    selectedColumns.push({
                        role: 'tooltip',
                        calc: (dt, row) => {
                            const rowValue = dt.getValue(row, 0);
                            const column = this.chartData.cols[visibleColumnIndex];

                            if (typeof rowValue !== 'string') {
                                return rowValue;
                            }

                            const currentDateString = moment(rowValue, this.momentDateFormat)
                                .startOf(this.dateFormat).format(mediumDateFormat);

                            let endDateString;
                            if (moment(rowValue, this.momentDateFormat).endOf(this.dateFormat).isAfter(moment())) {
                                endDateString = moment().format(mediumDateFormat);
                            } else {
                                endDateString = moment(rowValue, this.momentDateFormat)
                                    .endOf(this.dateFormat)
                                    .format(mediumDateFormat);
                            }

                            return `${currentDateString} - ${endDateString}\n${column.label}: ${
                                dt.getFormattedValue(row, visibleColumnIndex)}`;
                        },
                        type: 'string'
                    });
                }
            });

            viewData.setColumns(selectedColumns);
        },
        setSelectedRows() {
            if (!this.viewData || !this.hasData) {
                return;
            }

            // set the rows to be shown in the data view when in total mode
            const selectedRows = this.selectedRows.map((visibleRow) => {
                const visibleRowIndex = this.chartData.rows
                    .findIndex((row) => row.c[0].id === visibleRow);

                return visibleRowIndex;
            });

            selectedRows.sort(((a, b) => a - b));

            this.viewData.setRows(selectedRows);
        },
        initSelection() {
            const groupChanged = this.group !== this.previousGroup;
            const totalModeChanged = this.totalMode !== this.previousTotalMode;
            const needsReselection = groupChanged || totalModeChanged;
            this.previousGroup = this.group;
            this.previousTotalMode = this.totalMode;

            if (!this.hasData) return;

            if (!needsReselection && this.preserveSelection) {
                if (!this.totalMode) {
                    this.selectedColumns = this.selectedColumns
                        .filter((column) => this.chartData.cols.some((col) => column === col.id));

                    if (this.selectedColumns.length) {
                        this.setSelectedColumns();
                        return;
                    }
                } else {
                    this.selectedRows = this.selectedRows
                        .filter((row) => this.chartData.rows.some((r) => row === r.c[0].id));

                    if (this.selectedRows.length) {
                        this.setSelectedRows();
                        return;
                    }
                }
            }

            // initialize the selection of legend options
            if (!this.totalMode) {
                this.initSelectedColumns();
                this.setSelectedColumns();
            } else {
                this.initSelectedRows();
                this.setSelectedRows();
            }
        },
        initSelectedColumns() {
            const selectFrom = 2;
            const selectTo = 7;

            if (this.chartData.cols.length === 2) {
                this.selectedColumns = ['totals'];
                return;
            }

            this.selectedColumns = this.chartData.cols
                .filter((_, index) => index >= selectFrom && index <= selectTo)
                .map((column) => column.id);
        },
        initSelectedRows() {
            const selectFrom = 1;
            const selectTo = 6;

            this.selectedColumns = ['totals', 'style'];

            if (this.chartData.rows.length === 1) {
                this.selectedRows = ['totals'];
                return;
            }

            this.selectedRows = this.chartData.rows
                .filter((_, index) => index >= selectFrom && index <= selectTo)
                .map((row) => row.c[0].id);
        },
        createDataTableAndView(chartData) {
            const dataTable = new chartsLib.visualization.DataTable(chartData, 0.6);
            this.formatColumns(dataTable);
            const viewData = new chartsLib.visualization.DataView(dataTable);

            return { dataTable, viewData };
        },
        initDataTable(chartData = this.chartData) {
            if (!chartsLib) return;

            this.dataTable = new chartsLib.visualization.DataTable(chartData, 0.6);
            this.formatColumns(this.dataTable);
            this.viewData = new chartsLib.visualization.DataView(this.dataTable);
        },
        formatColumns(dataTable = this.dataTable) {
            if (!chartsLib || this.loading || !this.hasData) return;

            const formatter = new chartsLib.visualization.NumberFormat({
                prefix: this.prefix,
                suffix: this.suffix,
                decimalSymbol: '.',
                groupingSymbol: ',',
                pattern: '#,###.##'
            });

            // format each of the columns that are of type number
            this.chartData.cols.forEach((column, index) => {
                if (column.type === 'number' && index !== 0) {
                    formatter.format(dataTable, index);
                }
            });
        },
        getAnimationChartData({ viewColumns }) {
            if (!this.chartData) return;
            let max = 0;

            return {
                rows: this.chartData.rows.map((row) => ({
                    c: [...row.c.map((val, index) => {
                        if (index === 0) return val;

                        if (val.v > max && viewColumns.includes(index)) max = val.v;

                        const value = (val.v * ((Math.random() * 0.25) + 0.75));

                        return index === 0 ? val : {
                            v: value
                        };
                    })]
                })),
                cols: this.chartData.cols,
                max
            };
        },
        drawChart({ preventDefaultAnimation = false, preventLineCustomAnimation = false } = {}) {
            if (!chartsLib || !this.chartObject || !this.hasData) return;
            const shouldDefaultAnimate = this.animate && this.type === 'LineChart';

            if (!this.viewData) {
                this.initDataTable();
                this.initSelection();
                this.setSelectedColumns();
            }

            this.$nextTick(() => {
                if (!this.hasData) return;

                if (shouldDefaultAnimate && !preventLineCustomAnimation) {
                    const viewData = this.viewData;
                    const dataTable = this.dataTable;
                    const viewColumns = this.viewData.getViewColumns();
                    const animationChartData = this.getAnimationChartData({ viewColumns });
                    this.initDataTable(animationChartData);
                    this.setSelectedColumns();
                    this.previousViewData = this.viewData;
                    this.viewData = viewData;
                    this.dataTable = dataTable;

                    this.chartObject.draw(this.previousViewData, {
                        ...this.chartOptions,
                        animation: undefined,
                        vAxis: {
                            ...this.chartOptions.vAxis,
                            maxValue: animationChartData.max
                        }
                    });
                    this.$nextTick(() => {
                        this.chartObject.draw(this.viewData, {
                            ...this.chartOptions,
                            vAxis: {
                                ...this.chartOptions.vAxis,
                                maxValue: animationChartData.max
                            }
                        });
                    });
                } else if (shouldDefaultAnimate && !preventDefaultAnimation) {
                    this.chartObject.draw(this.viewData, this.chartOptions);
                } else {
                    this.chartObject.draw(this.viewData, {
                        ...this.chartOptions,
                        animation: preventDefaultAnimation ? undefined : this.chartOptions.animation
                    });
                }
            });
        },
        createChartObject() {
            const createChart = (el, google, type) => {
                if (!type || !google || !el) return;
                return new google.visualization[type](el);
            };
            const fn = this.createChart || createChart;
            this.chartObject = fn(this.$refs.chart, chartsLib, this.type);
            this.attachListeners();
            return this.chartObject;
        },
        attachListeners() {
            if (!this.events) return;
            Object.entries(this.events).forEach(([event, listener]) => {
                chartsLib.visualization.events.addListener(this.chartObject, event, listener);
            });
        },
        highlight(index) {
            if (this.totalMode) {
                this.highlightRow(index);
            } else {
                this.highlightColumn(index);
            }
        },
        highlightRow(rowIndex) {
            if (!this.chartObject) return;
            const indexToHighlight = this.viewData.getViewRowIndex(rowIndex);

            if (indexToHighlight || indexToHighlight === 0) {
                // the setSelection function sets the currently highlighted column
                try {
                    this.chartObject.setSelection([{ row: indexToHighlight }]);
                } catch {}
            }
        },
        highlightColumn(columnIndex) {
            if (!this.chartObject) return;
            const indexToHighlight = this.viewData.getViewColumnIndex(columnIndex + 1);

            if (indexToHighlight) {
                // the setSelection function sets the currently highlighted column
                try {
                    this.chartObject.setSelection([{ column: indexToHighlight }]);
                } catch {}
            }
        },
        clearColumnHighlight() {
            if (!this.chartObject) return;
            try {
                this.chartObject.setSelection([]);
            } catch {}
        },
        resolveTotalModeChartData() {
            const columns = [{
                id: 'hAxis',
                label: 'hAxis',
                type: this.hAxisFormat
            },
            {
                id: 'totals',
                label: this.sumLabel,
                type: 'number'
            },
            {
                id: 'style',
                type: 'string',
                role: 'style'
            }];

            let columnData = this.data[this.sumFieldName];
            const singleColumn = !Array.isArray(columnData);

            if (singleColumn) {
                columnData = [{ id: 'totals', value: columnData, displayName: this.sumLabel }];
            }

            // assign colors to the rows
            getColors(columnData.map(({ id }) => id), this.group);

            const rows = columnData.map(({ id, value, displayName }) => ({
                c: [
                    { v: displayName, id },
                    { v: value },
                    { v: getColor(id, this.group) }
                ]
            }));

            return {
                rows,
                cols: columns
            };
        },
        resolveColumnArrayChartData() {
            const columns = [{
                id: 'hAxis',
                label: 'hAxis',
                type: this.hAxisFormat
            },
            ...this.data[this.sumFieldName].map(({ id, displayName }) => ({
                id,
                label: displayName,
                type: 'number'
            }))
            ];

            const rows = Object.keys(this.data)
                .filter((key) => key !== this.sumFieldName)
                .map((key) => {
                    const dataRow = this.data[key];

                    const row = [{
                        v: moment(key).format(this.momentDateFormat)
                    }];

                    dataRow.forEach((value) => {
                        row.push({
                            v: value
                        });
                    });

                    return {
                        c: row
                    };
                });

            return {
                rows,
                cols: columns
            };
        },
        resolveSingleColumnChartData() {
            const columns = [{
                id: 'hAxis',
                label: 'hAxis',
                type: this.hAxisFormat
            },
            {
                id: 'totals',
                label: this.sumLabel,
                type: 'number'
            }];

            const rows = Object.keys(this.data)
                .filter((key) => key !== this.sumFieldName)
                .map((key) => ({
                    c: [{
                        v: Number(key),
                        f: this.dateColumns ? moment(key).format(this.momentDateFormat) : `${this.title}: ${key}`
                    },
                    { v: this.data[key] }]
                }));

            return {
                rows,
                cols: columns
            };
        }
    },
    computed: {
        hasData() {
            return !!this.data;
        },
        sumLabel() {
            return this.mean ? 'Mean' : 'Sum';
        },
        arrayOfColumns() {
            return Array.isArray(this.data[this.sumFieldName]);
        },
        /**
         * converts a data object to a chartData object in 3 methods, total mode, column array and a single column
         * ({ 'date': [1,2,3], 'date2': [1,2,3] } -> { rows: [{ c: [{ v: rowValue }]}], cols: [{}]}})
         */
        chartData() {
            if (!this.hasData) return;

            if (this.totalMode) {
                return this.resolveTotalModeChartData();
            }

            if (!this.arrayOfColumns) {
                return this.resolveSingleColumnChartData();
            }

            return this.resolveColumnArrayChartData();
        },
        legendOptions() {
            if (!this.hasData || this.loading) return [];

            const arrayOfColumns = Array.isArray(this.data[this.sumFieldName]);

            if (!arrayOfColumns) {
                return [{
                    id: 'totals',
                    label: this.sumLabel,
                    type: 'number'
                }];
            }

            return this.data[this.sumFieldName]
                .map(({ id, displayName }) => ({ id, label: displayName }))
                .filter((option) => !!option.id);
        },
        legendColors() {
            return getColors(this.legendOptions.map((option) => option.id), this.group);
        },
        chartOptions() {
            const options = {
                fontName: 'Open Sans',
                chartArea: {
                    left: '6%',
                    top: 4,
                    width: '90%',
                    height: '84%'
                },
                height: 350,
                lineWidth: 2,
                pointSize: 5,
                legend: 'none',
                hAxis: {
                    title: this.title,
                    textStyle: {
                        fontSize: 12,
                        color: '#8ca0b3',
                        bold: false
                    },
                    titleTextStyle: {
                        bold: true,
                        italic: false,
                        color: '#828d9a',
                        fontSize: 12
                    },
                    gridlines: {
                        color: 'transparent',
                        count: 12
                    },
                    maxAlternation: 1, // use a maximum of 1 line of labels
                    maxTextLines: this.showAllHAxisValues ? 'auto' : 1,
                    showTextEvery: this.showTextEvery, // show every label if possible
                    minTextSpacing: 8, // minimum spacing between adjacent labels, in pixels
                    baselineColor: 'transparent',
                    slantedText: false
                },
                vAxis: {
                    textStyle: {
                        fontSize: 12,
                        color: '#8ca0b3',
                        bold: false
                    },
                    titleTextStyle: {
                        bold: true,
                        italic: false,
                        color: '#828d9a',
                        fontSize: 12
                    },
                    gridlines: {
                        color: '#e1e6eb',
                        count: 5
                    },
                    baselineColor: '#e1e6eb',
                    minValue: 0,
                    viewWindowMode: 'pretty',
                    viewWindow: {
                        min: 0
                    },
                    format: 'short',
                    minorGridlines: { count: 0 }
                },
                bar: { groupWidth: '25%' },
                isStacked: this.stacked || this.chartStacked,
                width: '100%'
            };

            if (!this.chartData) return options;

            if (!this.totalMode) {
                // set colors for columns when not in totalMode (row view)
                options.series = {
                    ...this.selectedColumns.map((col) => {
                        if (col === 'totals') {
                            return {
                                pointSize: this.chartData.rows.length > 50 ? 0 : 6,
                                color: getColor(col, this.group),
                                lineWidth: 3,
                                type: this.type === 'ComboChart' ? 'line' : undefined
                            };
                        }

                        return {
                            color: getColor(col, this.group),
                            type: this.type === 'ComboChart' ? 'area' : undefined
                        };
                    })
                };
            }

            const selectedColumnsIndexes = !this.totalMode && this.selectedColumns
                .map((selectedId) => this.chartData.cols.findIndex((column) => column.id === selectedId));
            const selectedRowsIndexes = this.totalMode && this.selectedRows.map((visibleRow) => this.chartData.rows
                .findIndex((row) => row.c[0].id === visibleRow));

            const isAllDataOnlyZeroes = this.chartData.rows.every((row, rowIndex) => {
                if (!this.totalMode) {
                    const [, ...rowData] = row.c;

                    return rowData
                        .every((item, index) => !selectedColumnsIndexes.includes(index + 1) || item.v === 0);
                }

                const [, ...rowData] = row.c;
                const rowColorDataIndex = rowData.length - 1;

                return !selectedRowsIndexes.includes(rowIndex)
                    || rowData.every((item, index) => index === rowColorDataIndex || item.v === 0);
            });

            /* there is a bug that ignores the vAxis.viewWindow.min value when all values in the chart are zeros
                so we set the max value explicitly in this case
                https://stackoverflow.com/questions/22017899
                https://stackoverflow.com/questions/56490778
             */
            if (isAllDataOnlyZeroes) {
                options.vAxis.viewWindow.max = 1;
            }

            // if there are more than 50 rows, make the points hidden
            if (this.chartData.rows.length > 50) {
                options.pointSize = 0;
            }

            if (this.animate) {
                options.animation = {
                    duration: 600,
                    easing: 'out'
                };
            }

            return merge(options, this.options);
        },
        chartStacked() {
            return this.type === 'AreaChart' || this.type === 'ComboChart';
        },
        momentDateFormat() {
            switch (this.dateFormat) {
            case 'week':
                return weekFormat;
            case 'month':
                return monthFormat;
            default:
            }

            return mediumDateFormat;
        }
    }
};
</script>

<style lang="scss" scoped>
.chart-container {
    display: flex;
    position: relative;
}

.chart {
    flex: 1;
    max-width: calc(100% - 213px);
    overflow: hidden;
}

.noDataWHeight, .loadingWrap.opacityOverlay {
    min-height: 200px;
    position: relative;
    width: calc(100% - 213px);
}
</style>
